<template>
  <v-card v-if="call" outlined class="mb-2">
    <v-card-text v-if="isMobile && call.name" class="text-center" style="width: 100%">{{ call.name }}</v-card-text>
    <v-toolbar flat v-if="!isMobile && (call.name || !call.retry_origin_history_id)" dense>
      <v-card-title v-if="call.name">{{ call.name }}</v-card-title>
      <v-spacer />
      <v-switch
        dense
        hide-details="auto"
        v-model="call.retry"
        class="mt-0"
        v-if="!call.retry_origin_history_id && !isMobile && !['relaunch_failed', 'relaunched'].includes(call.status)"
      >
        <template v-slot:label>
          <v-card-text style="white-space: break-spaces" class="input__label pl-0 pr-2">
            {{ $t('triggersHistories.includeInRelaunch') }}
          </v-card-text>
        </template>
      </v-switch>
    </v-toolbar>
    <v-container fluid v-if="isMobile" class="pb-0">
      <v-row>
        <v-col md="6" class="pb-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-action-text>
                <span class="font-weight-black"> {{ $t('triggersHistories.reference') }}: </span>
                {{ call._id }}
              </v-list-item-action-text>
              <v-list-item-action-text>
                <span class="font-weight-black"> {{ $t('triggersHistories.type') }}: </span>
                {{ call.type }}
              </v-list-item-action-text>
            </v-list-item-content>
          </v-list-item>
        </v-col>
        <v-col md="6" class="text-right pb-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-action-text>
                <span class="font-weight-black"> {{ $t('triggersHistories.status') }}: </span>
                {{ getStatusTranslation(call.status) }}
              </v-list-item-action-text>
              <span v-if="call.retry_origin_history_id">
                <router-link
                  style="font-size: 0.75rem"
                  :key="$route.fullPath"
                  :to="{ name: 'TriggerHistoryDetails', params: { id: call.retry_origin_history_id } }"
                >
                  {{ $t('triggersHistories.seeRelaunch') }}
                </router-link>
              </span>
              <v-list-item-action-text v-if="call.relaunch_failure_reason">
                {{ getRelaunchFailureReasonTranslation(call.relaunch_failure_reason) }}
              </v-list-item-action-text>
            </v-list-item-content>
          </v-list-item>
        </v-col>
      </v-row>
      <v-list-item>
        <v-list-item-content class="pb-0 pt-0">
          <v-list-item-action-text v-if="call.call_parameters.url">
            <span class="font-weight-black"> {{ $t('triggersHistories.url') }}: </span>
            {{ call.call_parameters.url }}
          </v-list-item-action-text>
          <v-list-item-action-text v-if="call.call_parameters.invocation_type">
            <span class="font-weight-black"> {{ $t('triggersHistories.invocationType') }}: </span>
            {{ call.call_parameters.invocation_type }}
          </v-list-item-action-text>
          <v-list-item-action-text v-if="call.call_parameters.log_type">
            <span class="font-weight-black"> {{ $t('triggersHistories.logType') }}: </span>
            {{ call.call_parameters.log_type }}
          </v-list-item-action-text>
          <v-list-item-action-text v-if="call.call_parameters.headers">
            <span class="font-weight-black"> {{ $t('triggersHistories.headers') }}: </span>
            {{ call.call_parameters.headers }}
          </v-list-item-action-text>
          <v-list-item-action-text v-if="call.call_parameters.body">
            <span class="font-weight-black"> {{ $t('triggersHistories.body') }}: </span>
            {{ call.call_parameters.body }}
          </v-list-item-action-text>
          <v-list-item-action-text v-if="call.call_parameters.http_method">
            <span class="font-weight-black"> {{ $t('triggersHistories.http_method') }}: </span>
            {{ call.call_parameters.http_method }}
          </v-list-item-action-text>
        </v-list-item-content>
      </v-list-item>
    </v-container>
    <v-container fluid v-else class="pb-0">
      <v-row>
        <v-col md="6" class="pb-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $t('triggersHistories.reference') }}: {{ call._id }}</v-list-item-title>
              <v-list-item-title>{{ $t('triggersHistories.type') }}: {{ call.type }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-col>
        <v-col md="6" class="text-right pb-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title v-if="call.status">
                {{ $t('triggersHistories.status') }}: {{ getStatusTranslation(call.status) }}
              </v-list-item-title>
              <span v-if="call.retry_origin_history_id">
                <router-link
                  :key="$route.fullPath"
                  :to="{ name: 'TriggerHistoryDetails', params: { id: call.retry_origin_history_id } }"
                  target="_blank"
                >
                  {{ $t('triggersHistories.seeRelaunch') }}
                </router-link>
              </span>
              <span v-if="call.relaunch_failure_reason">
                {{ getRelaunchFailureReasonTranslation(call.relaunch_failure_reason) }}
              </span>
            </v-list-item-content>
          </v-list-item>
        </v-col>
      </v-row>
      <v-list-item>
        <v-list-item-content class="pb-0 pt-0">
          <v-list-item-subtitle v-if="call.call_parameters.url">
            <span class="font-weight-black"> {{ $t('triggersHistories.url') }}: </span>
            {{ call.call_parameters.url }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="call.call_parameters.invocation_type">
            <span class="font-weight-black"> {{ $t('triggersHistories.invocationType') }}: </span>
            {{ call.call_parameters.invocation_type }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="call.call_parameters.log_type">
            <span class="font-weight-black"> {{ $t('triggersHistories.logType') }}: </span>
            {{ call.call_parameters.log_type }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="call.call_parameters.headers">
            <span class="font-weight-black"> {{ $t('triggersHistories.headers') }}: </span>
            {{ call.call_parameters.headers }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="call.call_parameters.body">
            <span class="font-weight-black"> {{ $t('triggersHistories.body') }}: </span>
            {{ call.call_parameters.body }}
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="call.call_parameters.http_method">
            <span class="font-weight-black"> {{ $t('triggersHistories.http_method') }}: </span>
            {{ call.call_parameters.http_method }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-container>
    <v-container fluid class="pt-0">
      <v-switch
        dense
        hide-details="auto"
        v-model="call.retry"
        class="mt-0 pl-3 mb-4"
        v-if="!call.retry_origin_history_id && isMobile && !['relaunch_failed', 'relaunched'].includes(call.status)"
      >
        <template v-slot:label>
          <v-list-item-action-text style="white-space: break-spaces" class="input__label font-weight-black text-center">
            {{ $t('triggersHistories.includeInRelaunch') }}
          </v-list-item-action-text>
        </template>
      </v-switch>
      <v-row>
        <v-col class="pl-0">
          <v-row justify="center">
            <v-btn
              v-if="executionsErrors.length"
              :small="isMobile"
              depressed
              tile
              @click="listDialog = true"
              class="mb-2"
            >
              {{ $t('triggersHistories.seeErrors') }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <errors-list-dialog
      v-model="listDialog"
      :items="executionsErrors"
      @close="listDialog = false"
      :headers="errorHeaders"
    />
  </v-card>
</template>

<script>
  import listDialog from '../dialogs/ListDialog.vue'

  export default {
    name: 'TriggerHistoryCall',
    components: {
      errorsListDialog: listDialog,
    },
    data() {
      return {
        targetedErrors: [],
        listDialog: false,
        errorHeaders: [
          {
            text: this.$t('triggersHistories.errorCode'),
            value: 'code',
            sortable: true,
          },
          {
            text: this.$t('triggersHistories.date'),
            value: 'date',
            sortable: true,
          },
          {
            text: this.$t('triggersHistories.errorMessage'),
            value: 'message',
            sortable: true,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ],
        status: [
          { text: this.$t('triggersHistories.processing'), value: 'processing' },
          { text: this.$t('triggersHistories.success'), value: 'success' },
          { text: this.$t('triggersHistories.error'), value: 'error' },
          { text: this.$t('triggersHistories.relaunched'), value: 'relaunched' },
          { text: this.$t('triggersHistories.relaunchFailed'), value: 'relaunch_failed' },
        ],
        relaunchFailureReasons: [{ text: this.$t('triggersHistories.callDeleted'), value: 'call_deleted' }],
      }
    },
    props: {
      call: {
        type: Object,
        required: true,
      },
      executionsErrors: {
        type: Array,
        required: true,
      },
    },
    methods: {
      getStatusTranslation: function (statusValue) {
        let status = this.status.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else {
          return statusValue
        }
      },
      getRelaunchFailureReasonTranslation: function (failureReasonValue) {
        let failureReason = this.relaunchFailureReasons.find(
          (failureReason) => failureReason.value === failureReasonValue
        )
        if (failureReason) {
          return failureReason.text
        } else {
          return failureReasonValue
        }
      },
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
    },
  }
</script>
